import PracticesAPI from 'api/PracticesAPI'
import PracticePlanEnum from '@/enums/PracticePlanEnum'
import { intervalToApi, isCustomPlan, isFreePlanCode, shouldUseSubscribe } from 'services/billingPlanHelpers'

/**
 * Build feature bullets for checkout sidebar from a plan catalog item.
 */
function buildPlanFeatureList(plan, t) {
    if (!plan) {
        return []
    }

    const features = []
    if (plan.code === PracticePlanEnum.FREE) {
        return [
            t('pages.practice.billing.billing_list.item_1'),
            t('pages.practice.billing.billing_list.item_2'),
            t('pages.practice.billing.billing_list.item_3'),
            t('pages.practice.billing.billing_list.item_4'),
            t('pages.practice.billing.billing_list.item_5'),
            t('pages.practice.billing.billing_list.item_6'),
        ]
    }

    features.push(t('pages.practice.billing.payment_form_list.item_1'))
    features.push(t('pages.practice.billing.payment_form_list.item_2'))
    features.push(t('pages.practice.billing.payment_form_list.item_3'))

    if (plan.includedSeats != null) {
        features.push(`Up to ${plan.includedSeats} provider seats included`)
    } else if (!plan.hasTieredLocations) {
        features.push('Unlimited provider seats')
    }

    if (plan.includedReferralsPerMonth != null && plan.includedReferralsPerMonth > 0) {
        features.push(`${plan.includedReferralsPerMonth.toLocaleString()} inbound referrals/mo included`)
    }

    if (plan.extraReferralPrice) {
        features.push(`$${plan.extraReferralPrice} per extra referral`)
    }

    if (plan.hasExtraSeats && plan.extraSeatMonthlyPrice) {
        features.push(`$${plan.extraSeatMonthlyPrice}/mo per extra seat`)
    }

    if (plan.hasTieredLocations) {
        features.push('Graduated per-location pricing')
        features.push('Confirmed-referral usage fees')
    }

    features.push(t('pages.practice.billing.payment_form_list.item_7'))
    features.push(t('pages.practice.billing.payment_form_list.item_8'))
    return features
}

function getCheckoutPrices(plan) {
    if (!plan) {
        return { monthlyPrice: null, yearlyPrice: null, hasFixedPrice: false }
    }
    if (plan.hasTieredLocations || plan.monthlyPrice == null) {
        return {
            monthlyPrice: plan.locationTiers?.[0]?.monthlyPrice ?? null,
            yearlyPrice: plan.locationAnnualTiers?.[0]?.annualPrice ?? null,
            hasFixedPrice: false,
        }
    }
    return {
        monthlyPrice: plan.monthlyPrice,
        yearlyPrice: plan.annualPrice,
        hasFixedPrice: true,
    }
}

function annualSavings(monthlyPrice, yearlyPrice) {
    if (monthlyPrice == null || yearlyPrice == null) {
        return 0
    }
    return Math.max(0, 12 * monthlyPrice - yearlyPrice)
}

/**
 * Subscribe (cutover / first paid) or change-plan depending on practice engine state.
 */
function submitPlanCheckout({ practice, plan, isAnnual, paymentMethodId, billingAddress }) {
    if (!plan || isCustomPlan(plan.code)) {
        return Promise.reject(new Error('Invalid plan for checkout'))
    }

    if (isFreePlanCode(plan.code)) {
        return PracticesAPI.subscribePlan({ planCode: plan.code })
    }

    const interval = intervalToApi(isAnnual)
    const useSubscribe = shouldUseSubscribe(practice)

    if (useSubscribe) {
        return PracticesAPI.subscribePlan({
            planCode: plan.code,
            interval,
            paymentMethodId,
            billingAddress,
        })
    }

    return PracticesAPI.changePlan({
        planCode: plan.code,
        interval,
    })
}

export { buildPlanFeatureList, getCheckoutPrices, annualSavings, submitPlanCheckout }
